/**
 * ESCHER FALLS — 12姿勢（方位4 × 仰角3）のスナップ遷移コントローラ。
 * VIEW-EXPANSION-SPEC §5 / PROJECTION-9VIEW §4.2。
 *
 * **カメラの position / up / lookAt の唯一の所有者**（frustum は render/scene.ts の領分）。
 * 入力イベントは扱わない（src/main.ts の責務）。
 *
 * ## 遷移の作り方
 * `(θ, φ)` = (方位角, 仰角) を**角度のまま**線形補間し、毎フレーム置き直す。
 * - 仰角遷移: θ 固定で φ を動かす。これはその鉛直平面内の**大円補間そのもの**
 *   （dir は単位ベクトルで φ が中心角）。`Quaternion.slerp` を組む必要はない。
 * - 方位遷移: φ 固定で θ を ±π/2 動かす。水平円弧。
 * - **position の直線補間は禁止**（軌道が円弧にならず正射影の見え方が崩れる）。
 * - **θ と φ を同時に動かしてはならない**（PROJECTION-9VIEW §3.8.6：
 *   方向が球面上の2次元パッチを掃き、ゴースト接続が爆発的に増える）。
 */

import * as THREE from 'three';
import { ROTATE_DURATION_MS } from '../core/types';
import type { Bounds, ViewPose } from '../core/types';
import {
  ELEVATION_ANGLES,
  azimuthAngle,
  changeElevation,
  poseFrame,
  rotateAzimuth,
  samePose,
} from '../core/projection';
import { cameraDistance } from './scene';
import { DEFAULT_REVEAL_OFFSET, type RevealOffset } from './revealDirection';

export interface CameraRig {
  /** 現在**確定している**姿勢。遷移中は遷移前の値のまま。 */
  readonly pose: ViewPose;
  /** 方位・仰角いずれかの遷移を補間中か。 */
  readonly isRotating: boolean;
  /** 放水中などで入力を封じているか。 */
  readonly isLocked: boolean;
  /**
   * 方位を回す。遷移中・ロック中は**何もせず false**（キューイングしない）。
   */
  rotate(dir: -1 | 1): boolean;
  /**
   * 仰角を変える。遷移中・ロック中、および**端（低より下・真上より上）では false**。
   */
  elevate(dir: -1 | 1): boolean;
  /**
   * 補間を dt (ms) 進める。
   * @returns **この呼び出しで遷移が完了したときだけ true**。それ以外は常に false。
   *          true を返した時点で `pose` は既に新しい値になっている。
   */
  update(dt: number): boolean;
  /** アニメーションなしで指定姿勢へ飛ぶ（レベルロード時）。 */
  snapTo(pose: ViewPose): void;
  /** レベル切替・リサイズ時に注視点と距離を更新する。 */
  setBounds(bounds: Bounds): void;
  /** 放水中のロック。進行中の遷移は最後まで完走する。 */
  lockCamera(): void;
  unlockCamera(): void;

  /** 種明かしの演出中か。 */
  readonly isRevealing: boolean;
  /**
   * **種明かし**を始める。通水した直後に呼ぶ（REVEAL_TOTAL_MS で終わる）。
   *
   * 解いた視点から少しだけずらし、錯視で繋がっていた水路が**実は宙を隔てている**
   * ことを見せてから、元の視点へ戻す。水は継ぎ目で途切れて向こう側から流れ出す
   * ように描かれているので、ずらすと「水が宙を飛び越えている」絵になる。
   *
   * **見た目だけの演出で、`pose` には一切触れない。** 遷移中・種明かし中は false。
   *
   * @param offset ずらす向きと量。面ごとに `revealDirection.chooseRevealOffset` で選ぶ。
   */
  reveal(offset?: RevealOffset): boolean;
  /** 種明かしを打ち切り、解いた視点へ即座に戻す。演出中でなければ何もしない。 */
  cancelReveal(): void;
  /**
   * 種明かしを dt (ms) 進める。**main がフレームごとに呼ぶ。**
   * 状態機械は `rotating` の間しか `update` を呼ばないので、`clear` 中の演出は
   * こちらで別に回す。戻り値も無い——**終わっても接続の再構築を誘発しない**ためである。
   */
  updateReveal(dt: number): void;
}

// =============================================================================
// 種明かし（REVEAL）
// =============================================================================
//
// 通水 → [解いた絵で止める] → [ずらす] → [ずれたまま止める] → [戻す] → 解いた絵。
// **遊んでいる最中は θ と φ を同時に動かしてはならない**（上の注記）が、
// それは接続判定を守るための決まりで、判定が済んだ `clear` の後には当たらない。
// ここでは斜めに動かすほうが、宙の隔たりがはっきり見える。

/** 解いた絵を見せたまま止めておく時間 (ms)。 */
export const REVEAL_HOLD_MS = 700;
/** 解いた視点からずらしきるまで (ms)。 */
export const REVEAL_OUT_MS = 1400;
/** ずれた位置で止めて、宙の隔たりを見せる時間 (ms)。 */
export const REVEAL_DWELL_MS = 800;
/** 解いた視点へ戻るまで (ms)。 */
export const REVEAL_BACK_MS = 1200;
/** 種明かしの全長 (ms)。main はこれを待ってから次の面へ送る。 */
export const REVEAL_TOTAL_MS = REVEAL_HOLD_MS + REVEAL_OUT_MS + REVEAL_DWELL_MS + REVEAL_BACK_MS;
/**
 * ずらしている間の拡大率。**frustum は 12 の正準姿勢でしか測っていない**ので、
 * その間の角度では盤面の端が画面からはみ出しうる。演出の間だけ少し引いて収める。
 */
const REVEAL_ZOOM = 0.86;

/** CORE-SPEC §5 のイージング。 */
function easeInOutCubic(u: number): number {
  return u < 0.5 ? 4 * u * u * u : 1 - Math.pow(-2 * u + 2, 3) / 2;
}

export function createCameraRig(
  camera: THREE.OrthographicCamera,
  bounds: Bounds,
): CameraRig {
  let pose: ViewPose = { azimuth: 0, elevation: 0 };
  let pendingPose: ViewPose = pose;
  let rotating = false;
  let locked = false;
  let elapsed = 0;

  // 補間の始点と終点。方位遷移では phi が、仰角遷移では theta が固定される。
  let thetaFrom = azimuthAngle(0);
  let thetaTo = thetaFrom;
  let phiFrom = ELEVATION_ANGLES[0];
  let phiTo = phiFrom;
  /** 現在の角度。`setBounds` でカメラを置き直すときに使う。 */
  let theta = thetaFrom;
  let phi = phiFrom;

  const center = new THREE.Vector3(bounds.center[0], bounds.center[1], bounds.center[2]);
  let dist = cameraDistance(bounds);

  const dirV = new THREE.Vector3();
  const upV = new THREE.Vector3();

  /** 種明かしの経過時間。null なら演出していない。 */
  let revealAt: number | null = null;
  let revealOffset: RevealOffset = DEFAULT_REVEAL_OFFSET;

  /**
   * 拡大率を置く。frustum は scene.ts の持ち物なので、触るのは zoom だけ。
   *
   * PROJECTION-VERIFIED §4.3 は `camera.zoom` を使うなと言うが、理由は
   * 「デバッグ表示の重ね絵がずれる」ことだけで、接続判定には響かない。
   * 種明かしは判定が済んだ `clear` の後にしか動かず、終われば必ず 1 に戻すので当たらない。
   */
  function setZoom(z: number): void {
    if (camera.zoom === z) return;
    camera.zoom = z;
    camera.updateProjectionMatrix();
  }

  /** 解いた視点（確定姿勢の正準角）へ戻し、演出を終える。 */
  function endReveal(): void {
    revealAt = null;
    setZoom(1);
    place(azimuthAngle(pose.azimuth), ELEVATION_ANGLES[pose.elevation]);
  }

  /**
   * カメラを (θ, φ) に置く。
   *
   * `up` は {@link poseFrame} が `dir ⊥ up` を恒等的に保証するので、
   * **真上でも `lookAt` が退化しない**。three.js は退化時に例外を投げず、
   * 視線を 1e-4 rad ずらして黙って別のカメラになる（PROJECTION-9VIEW §3.9.2）。
   * `(0,1,0)` を up に渡す実装に戻してはならない。
   */
  function place(t: number, p: number): void {
    theta = t;
    phi = p;
    const frame = poseFrame(t, p);
    dirV.set(frame.dir[0], frame.dir[1], frame.dir[2]);
    upV.set(frame.up[0], frame.up[1], frame.up[2]);
    camera.up.copy(upV);
    camera.position.copy(center).addScaledVector(dirV, dist);
    camera.lookAt(center);
    // デバッグ表示が render 前に投影しても正しい値になるよう確定させる。
    camera.updateMatrixWorld(true);
  }

  /** 遷移を開始する。始点は必ず「現在の確定姿勢の正準角」に取る。 */
  function begin(next: ViewPose): boolean {
    if (rotating || locked) return false;
    if (samePose(next, pose)) return false; // 端で動かなかった場合
    thetaFrom = azimuthAngle(pose.azimuth);
    phiFrom = ELEVATION_ANGLES[pose.elevation];
    thetaTo = azimuthAngle(next.azimuth);
    phiTo = ELEVATION_ANGLES[next.elevation];
    elapsed = 0;
    rotating = true;
    pendingPose = next;
    return true;
  }

  place(thetaFrom, phiFrom);

  return {
    get pose(): ViewPose {
      return pose;
    },
    get isRotating(): boolean {
      return rotating;
    },
    get isLocked(): boolean {
      return locked;
    },

    rotate(dir: -1 | 1): boolean {
      return begin(rotateAzimuth(pose, dir));
    },

    elevate(dir: -1 | 1): boolean {
      // changeElevation は端では同じ姿勢を返すので、begin が false を返す。
      return begin(changeElevation(pose, dir));
    },

    update(dt: number): boolean {
      if (!rotating) return false; // 静止中はカメラを一切触らない
      elapsed += Number.isFinite(dt) && dt > 0 ? dt : 0;

      if (elapsed >= ROTATE_DURATION_MS) {
        pose = pendingPose;
        rotating = false;
        // 正準角に置き直して浮動小数の累積を断つ。
        place(azimuthAngle(pose.azimuth), ELEVATION_ANGLES[pose.elevation]);
        return true; // 完了を返すのはこの1回きり
      }

      const e = easeInOutCubic(elapsed / ROTATE_DURATION_MS);
      place(thetaFrom + (thetaTo - thetaFrom) * e, phiFrom + (phiTo - phiFrom) * e);
      return false;
    },

    snapTo(next: ViewPose): void {
      revealAt = null;
      setZoom(1);
      rotating = false;
      elapsed = 0;
      pose = next;
      pendingPose = next;
      thetaFrom = azimuthAngle(next.azimuth);
      thetaTo = thetaFrom;
      phiFrom = ELEVATION_ANGLES[next.elevation];
      phiTo = phiFrom;
      place(thetaFrom, phiFrom);
    },

    setBounds(next: Bounds): void {
      center.set(next.center[0], next.center[1], next.center[2]);
      dist = cameraDistance(next);
      place(theta, phi); // 補間中でも現在の角度のまま置き直す
    },

    lockCamera(): void {
      locked = true;
    },

    unlockCamera(): void {
      locked = false;
    },

    get isRevealing(): boolean {
      return revealAt !== null;
    },

    reveal(offset?: RevealOffset): boolean {
      if (rotating || revealAt !== null) return false;
      revealAt = 0;
      if (offset !== undefined) revealOffset = offset;
      else {
        // 向きの指定が無ければ既定。真上からさらに上へは回れないので下へ覗く。
        const top = pose.elevation >= ELEVATION_ANGLES.length - 1;
        revealOffset = top
          ? { ...DEFAULT_REVEAL_OFFSET, dPhi: -DEFAULT_REVEAL_OFFSET.dPhi }
          : DEFAULT_REVEAL_OFFSET;
      }
      return true;
    },

    cancelReveal(): void {
      if (revealAt === null) return;
      endReveal();
    },

    updateReveal(dt: number): void {
      if (revealAt === null) return;
      revealAt += Number.isFinite(dt) && dt > 0 ? dt : 0;
      const t = revealAt;
      if (t >= REVEAL_TOTAL_MS) {
        endReveal();
        return;
      }
      // 0（解いた視点）〜 1（ずらしきった位置）の進み具合。
      let k: number;
      if (t < REVEAL_HOLD_MS) k = 0;
      else if (t < REVEAL_HOLD_MS + REVEAL_OUT_MS) {
        k = easeInOutCubic((t - REVEAL_HOLD_MS) / REVEAL_OUT_MS);
      } else if (t < REVEAL_HOLD_MS + REVEAL_OUT_MS + REVEAL_DWELL_MS) k = 1;
      else {
        const back = t - (REVEAL_HOLD_MS + REVEAL_OUT_MS + REVEAL_DWELL_MS);
        k = 1 - easeInOutCubic(back / REVEAL_BACK_MS);
      }
      setZoom(1 + (REVEAL_ZOOM - 1) * k);
      place(
        azimuthAngle(pose.azimuth) + revealOffset.dTheta * k,
        ELEVATION_ANGLES[pose.elevation] + revealOffset.dPhi * k,
      );
    },
  };
}
